import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

/**
 * Admin editor for the signup form config (cart sizes, service types,
 * per-service plans). Renders as a Card block on the Settings page.
 *
 * Backed by:
 *   GET  /api/settings/signup-form  (fetch current config with defaults)
 *   PUT  /api/settings/signup-form  (save)
 *
 * On the customer-facing wizard, this same shape is read via
 *   GET /api/public/signup-form-config
 */
export default function SignupFormSettingsCard() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings/signup-form").then(({ data }) => setCfg(data));
    }, []);

    if (!cfg) {
        return (
            <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">Loading signup form…</CardContent>
            </Card>
        );
    }

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/settings/signup-form", cfg);
            setCfg(data);
            toast.success("Signup form settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    // ---- cart sizes -----
    const addCart = () => setCfg((c) => ({
        ...c, cart_sizes: [...(c.cart_sizes || []), { value: "", label: "" }],
    }));
    const updateCart = (i, key, val) => setCfg((c) => ({
        ...c,
        cart_sizes: c.cart_sizes.map((s, idx) => idx === i ? { ...s, [key]: val } : s),
    }));
    const removeCart = (i) => setCfg((c) => ({
        ...c, cart_sizes: c.cart_sizes.filter((_, idx) => idx !== i),
    }));

    // ---- service types -----
    const updateST = (id, key, val) => setCfg((c) => ({
        ...c,
        service_types: c.service_types.map((s) => s.id === id ? { ...s, [key]: val } : s),
    }));

    // ---- plans -----
    const addPlan = (stId) => setCfg((c) => ({
        ...c,
        plans: {
            ...c.plans,
            [stId]: [...(c.plans[stId] || []),
                     { id: `plan_${Date.now()}`, label: "", desc: "", rate: 0 }],
        },
    }));
    const updatePlan = (stId, i, key, val) => setCfg((c) => ({
        ...c,
        plans: {
            ...c.plans,
            [stId]: c.plans[stId].map((p, idx) => idx === i
                ? { ...p, [key]: key === "rate" ? Number(val) || 0 : val } : p),
        },
    }));
    const removePlan = (stId, i) => setCfg((c) => ({
        ...c,
        plans: { ...c.plans, [stId]: c.plans[stId].filter((_, idx) => idx !== i) },
    }));

    return (
        <Card data-testid="signup-form-settings-card">
            <CardContent className="p-6 space-y-6">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="overline">Signup form</div>
                        <h3 className="font-heading text-lg font-semibold">Website signup wizard</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Controls the cart sizes, service types, and pricing shown on the
                            public signup form at <code>/signup</code>.
                        </p>
                    </div>
                    <Button size="sm" onClick={save} disabled={saving} data-testid="signup-form-save">
                        <Save className="w-4 h-4 mr-1" />
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>

                {/* Cart sizes */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Cart sizes offered
                        </Label>
                        <Button size="sm" variant="outline" onClick={addCart} data-testid="signup-form-add-cart">
                            <Plus className="w-3.5 h-3.5 mr-1" />Add cart size
                        </Button>
                    </div>
                    {cfg.cart_sizes.length === 0 && (
                        <div className="text-xs text-muted-foreground">No cart sizes — customers won't see a picker.</div>
                    )}
                    {cfg.cart_sizes.map((c, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center">
                            <Input value={c.value} placeholder="value (e.g. 96gal)"
                                   onChange={(e) => updateCart(i, "value", e.target.value)}
                                   data-testid={`signup-form-cart-value-${i}`} />
                            <Input value={c.label} placeholder="label (e.g. 96 gallon)"
                                   onChange={(e) => updateCart(i, "label", e.target.value)}
                                   data-testid={`signup-form-cart-label-${i}`} />
                            <Button size="icon" variant="ghost"
                                    onClick={() => removeCart(i)}
                                    data-testid={`signup-form-cart-remove-${i}`}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                        </div>
                    ))}
                </div>

                {/* Service types + plans */}
                <div className="space-y-4">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                        Service types
                    </Label>
                    {cfg.service_types.map((st) => (
                        <div key={st.id} className="border border-border rounded-md p-4 space-y-3">
                            <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-start">
                                <div className="space-y-2 min-w-0">
                                    <div className="text-xs font-mono text-muted-foreground">
                                        {st.id}
                                    </div>
                                    <Input value={st.label}
                                           placeholder="Public label"
                                           onChange={(e) => updateST(st.id, "label", e.target.value)}
                                           data-testid={`signup-form-st-label-${st.id}`} />
                                    <Input value={st.description}
                                           placeholder="Short description"
                                           onChange={(e) => updateST(st.id, "description", e.target.value)}
                                           data-testid={`signup-form-st-desc-${st.id}`} />
                                </div>
                                <div className="text-xs flex flex-col items-center gap-1">
                                    <Switch
                                        checked={!!st.enabled}
                                        onCheckedChange={(v) => updateST(st.id, "enabled", v)}
                                        data-testid={`signup-form-st-enabled-${st.id}`}
                                    />
                                    <span className="text-muted-foreground">Enabled</span>
                                </div>
                                <div className="text-xs flex flex-col items-center gap-1">
                                    <Switch
                                        checked={!!st.quote_only}
                                        onCheckedChange={(v) => updateST(st.id, "quote_only", v)}
                                        data-testid={`signup-form-st-quote-${st.id}`}
                                    />
                                    <span className="text-muted-foreground">Quote only</span>
                                </div>
                            </div>

                            {/* Plans — hidden for quote-only services */}
                            {!st.quote_only && (
                                <div className="space-y-2 pt-2 border-t border-border">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                            Plans
                                        </Label>
                                        <Button size="sm" variant="outline"
                                                onClick={() => addPlan(st.id)}
                                                data-testid={`signup-form-add-plan-${st.id}`}>
                                            <Plus className="w-3.5 h-3.5 mr-1" />Add plan
                                        </Button>
                                    </div>
                                    {(cfg.plans[st.id] || []).map((p, i) => (
                                        <div key={i} className="grid grid-cols-[1fr_1.5fr_2fr_100px_40px] gap-2 items-center">
                                            <Input value={p.id} placeholder="id"
                                                   onChange={(e) => updatePlan(st.id, i, "id", e.target.value)}
                                                   data-testid={`signup-form-plan-id-${st.id}-${i}`} />
                                            <Input value={p.label} placeholder="Label"
                                                   onChange={(e) => updatePlan(st.id, i, "label", e.target.value)}
                                                   data-testid={`signup-form-plan-label-${st.id}-${i}`} />
                                            <Input value={p.desc} placeholder="Short description"
                                                   onChange={(e) => updatePlan(st.id, i, "desc", e.target.value)}
                                                   data-testid={`signup-form-plan-desc-${st.id}-${i}`} />
                                            <Input type="number" value={p.rate}
                                                   placeholder="Rate"
                                                   onChange={(e) => updatePlan(st.id, i, "rate", e.target.value)}
                                                   data-testid={`signup-form-plan-rate-${st.id}-${i}`} />
                                            <Button size="icon" variant="ghost"
                                                    onClick={() => removePlan(st.id, i)}
                                                    data-testid={`signup-form-plan-remove-${st.id}-${i}`}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </Button>
                                        </div>
                                    ))}
                                    {(cfg.plans[st.id] || []).length === 0 && (
                                        <div className="text-xs text-muted-foreground italic">
                                            No plans — this service will look empty on the wizard. Enable
                                            "Quote only" or add a plan.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="pt-2 border-t border-border flex justify-end">
                    <Button onClick={save} disabled={saving} data-testid="signup-form-save-bottom">
                        <Save className="w-4 h-4 mr-1" />
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
